import _ from "lodash";
import { useState } from "react";

import { Box, Typography, useTheme } from "@mui/material";
import { Chip } from "@mui/material";

import { CompanyType } from "@interfaces/company.type";

import CompanyDetailsDrawerStyles from "./CompanyDetailsDrawerStyles";

interface IOverviewProperties {
  company: CompanyType;
}

export default function Overview({ company }: IOverviewProperties) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  let transformedIndustries = "";
  if (company.industries) {
    const capitalizedIndustries = [];
    for (const industry of company.industries) {
      capitalizedIndustries.push(_.capitalize(industry));
    }
    transformedIndustries = capitalizedIndustries.join(" ");
  }

  const addressParts = [company.city, company.state, company.country].filter(Boolean).join(", ");
  const testVariable = true;

  return (
    <Box sx={{ height: "100%", overflowY: "auto" }}>
      {company.apolloId && (
        <Box sx={{ ...CompanyDetailsDrawerStyles.dataContainer, pt: 4 }}>
          {company.shortDescription && (
            <Box sx={{ ...CompanyDetailsDrawerStyles.data, minWidth: "100%" }}>
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                }}
              >
                <Typography>
                  <strong>Description</strong>
                </Typography>
                <Typography
                  onClick={handleToggle}
                  sx={{
                    "&:hover": {
                      textDecoration: "underline",
                    },
                    color: theme.palette.info.main,
                    cursor: "pointer",
                  }}
                  variant="subtitle2"
                >
                  <strong>{isExpanded ? "(see less)" : "(see more)"}</strong>
                </Typography>
              </Box>

              <Typography
                sx={{
                  display: "-webkit-box",
                  height: isExpanded ? "auto" : theme.spacing(3),
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: isExpanded ? "none" : 1,
                  whiteSpace: "normal",
                }}
                variant="subtitle2"
              >
                {company.shortDescription}
              </Typography>
            </Box>
          )}
          {company.estimatedNumEmployees && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="subtitle1">
                <strong># Employees:</strong>
              </Typography>
              <Typography variant="body1">{company.estimatedNumEmployees.toLocaleString()}</Typography>
            </Box>
          )}
          {company.foundedYear && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="subtitle1">
                <strong>Founded:</strong>
              </Typography>
              <Typography variant="body1">{company.foundedYear}</Typography>
            </Box>
          )}
          {company.annualRevenuePrinted && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="subtitle1">
                <strong>Annual Revenue:</strong>
              </Typography>
              <Typography variant="body1">${company.annualRevenuePrinted}</Typography>
            </Box>
          )}
          {company.industries && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="subtitle1">
                <strong>Industry:</strong>
              </Typography>
              <Typography variant="body1">{transformedIndustries}</Typography>
            </Box>
          )}
          {company.publiclyTradedSymbol && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="subtitle1">
                <strong>Trade Symbol:</strong>
              </Typography>
              <Typography variant="body1">{company.publiclyTradedSymbol}</Typography>
            </Box>
          )}
          {company.publiclyTradedExchange && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="subtitle1">
                <strong>Traded Exchange:</strong>
              </Typography>
              <Typography variant="body1">{company.publiclyTradedExchange.toUpperCase()}</Typography>
            </Box>
          )}
          {addressParts && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="subtitle1">
                <strong>Headquarters:</strong>
              </Typography>
              <Typography variant="body1">{addressParts}</Typography>
            </Box>
          )}
        </Box>
      )}
      {!company.apolloId && (
        <Box>
          <Box sx={{ display: "block", mb: 8, pl: 2, pt: 4 }}>
            <Chip
              color="info"
              label="Awaiting Enrichment"
              size="medium"
              sx={{ fontSize: "14px", letterSpacing: "0.8px" }}
            />
          </Box>
          <Box sx={{ display: "block", pl: 2, pt: 8 }}>
            <Typography sx={{ fontWeight: 600 }} variant="body1">
              This company was recently added to the platform
            </Typography>
            <Typography variant="body1">
              Details for this company will be available soon. Some insights are limited for now, but you can still
              explore:
            </Typography>
            <ul>
              <li>Data Sources </li>
              <li>Patents</li>
              <li>AI Insights</li>
              <li>News</li>
            </ul>
          </Box>
        </Box>
      )}
    </Box>
  );
}
